import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import type { MarketInbox } from "../../shared/market";
import { useAuth } from "../auth/AuthProvider";
import { claimPush } from "../lib/push";
import { marketApi } from "./api";

const POLL_MS = 60_000;
const EMPTY: MarketInbox = { purchases: 0, buyerOffers: 0, sales: 0, sellerOffers: 0, total: 0, shop: false };

interface InboxValue {
  inbox: MarketInbox;
  /** Re-reads the counts (after opening an order, answering an offer…). */
  refresh: () => void;
}

const InboxContext = createContext<InboxValue>({ inbox: EMPTY, refresh: () => {} });

/** The badges' counts for the signed-in user: read every minute while the page is visible, and on return. */
export function InboxProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [inbox, setInbox] = useState<MarketInbox>(EMPTY);

  const refresh = useCallback(() => {
    if (!user) return;
    marketApi.inbox().then(setInbox, () => {});
  }, [user]);

  useEffect(() => {
    if (!user) {
      setInbox(EMPTY);
      return;
    }
    refresh();
    void claimPush().catch(() => undefined);
    const tick = () => document.visibilityState === "visible" && refresh();
    const timer = setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [user, refresh]);

  // The installed app's icon shows the count too, where the system supports it.
  useEffect(() => {
    const nav = navigator as Navigator & { setAppBadge?: (n: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
    if (inbox.total > 0) nav.setAppBadge?.(inbox.total).catch(() => {});
    else nav.clearAppBadge?.().catch(() => {});
  }, [inbox.total]);

  return <InboxContext.Provider value={{ inbox, refresh }}>{children}</InboxContext.Provider>;
}

export const useInbox = () => useContext(InboxContext);

/** A small red count next to a tab or link; nothing when zero. */
export function CountBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="count-badge">
      {count > 99 ? "99+" : count}
    </span>
  );
}
